import api from './api';

// ========================================
// MULTI-CHANNEL COMMUNICATION APIs
// ========================================

export type CommunicationMode = 'demo' | 'live';

export interface CommunicationConfig {
  mode?: CommunicationMode; // 'demo' or 'live'
  email: {
    enabled: boolean;
    smtpHost?: string;
    smtpPort?: number;
    smtpUsername?: string;
    smtpPassword?: string;
    fromEmail?: string;
    fromName?: string;
  };
  whatsapp: {
    enabled: boolean;
    provider?: 'twilio' | 'whatsapp_business';
    twilioAccountSid?: string;
    twilioAuthToken?: string;
    twilioWhatsAppNumber?: string;
    whatsappBusinessApiKey?: string;
    whatsappBusinessPhoneNumberId?: string;
  };
  sms: {
    enabled: boolean;
    provider?: 'twilio';
    twilioAccountSid?: string;
    twilioAuthToken?: string;
    twilioPhoneNumber?: string;
  };
}

export interface SendMessageRequest {
  userId?: number; // Optional - for admin endpoint fallback
  to?: string; // Required for demo/live mode - actual phone/email
  channel: 'email' | 'whatsapp' | 'sms';
  subject?: string; // For email
  message: string;
  attachments?: Array<{
    filename: string;
    content: string; // Base64 encoded
    contentType: string;
  }>;
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
  messageId?: string;
  channel?: string;
  error?: string;
}

export interface CommunicationHistory {
  id: number;
  userId: number;
  userName: string;
  channel: 'email' | 'whatsapp' | 'sms';
  subject?: string;
  message: string;
  status: 'sent' | 'failed' | 'pending';
  sentAt: string;
  error?: string;
}

// Get current mode from localStorage or default to 'demo'
const getCurrentMode = (): CommunicationMode => {
  const stored = localStorage.getItem('communicationMode');
  return (stored === 'demo' || stored === 'live') ? stored : 'demo';
};

// Set mode in localStorage
const setCurrentMode = (mode: CommunicationMode) => {
  localStorage.setItem('communicationMode', mode);
};

export const communicationApi = {
  // Get current mode
  getMode: (): CommunicationMode => getCurrentMode(),
  
  // Set mode
  setMode: (mode: CommunicationMode) => {
    setCurrentMode(mode);
  },

  // Get communication configuration
  getConfig: async (): Promise<{
    success: boolean;
    data: CommunicationConfig;
  }> => {
    const response = await api.get('/admin/communication/config');
    return response.data;
  },

  // Update communication configuration
  updateConfig: async (config: Partial<CommunicationConfig>): Promise<{
    success: boolean;
    message: string;
    data: CommunicationConfig;
  }> => {
    const response = await api.put('/admin/communication/config', config);
    return response.data;
  },

  // Test communication channel
  testChannel: async (channel: 'email' | 'whatsapp' | 'sms', testData: {
    to?: string;
    testMessage?: string;
  }): Promise<{
    success: boolean;
    message: string;
  }> => {
    const mode = getCurrentMode();
    if (mode === 'demo' && channel === 'sms') {
      // Use Twilio demo endpoint for SMS in demo mode
      if (!testData.to) {
        throw new Error("'to' and 'body' are required");
      }
      const response = await api.post('/sms/test/send', {
        to: testData.to,
        body: testData.testMessage || 'Test SMS from customer support platform',
      });
      return {
        success: response.data.success !== false,
        message: response.data.message || 'Test SMS sent successfully',
      };
    }
    const response = await api.post(`/admin/communication/test/${channel}`, testData);
    return response.data;
  },

  // Send message to user
  sendMessage: async (request: SendMessageRequest): Promise<SendMessageResponse> => {
    const mode = getCurrentMode();
    
    // Validate that 'to' is provided for demo/live mode
    if ((mode === 'demo' || mode === 'live') && !request.to) {
      throw new Error(`'to' field is required for ${mode} mode. Please provide phone number or email address.`);
    }
    
    if (mode === 'demo') {
      // Demo mode: Use Twilio demo endpoints for SMS
      if (request.channel === 'sms') {
        if (!request.to) {
          throw new Error("'to' and 'body' are required");
        }
        const response = await api.post('/sms/test/send', {
          to: request.to,
          body: request.message,
        });
        return {
          success: response.data.success !== false,
          message: response.data.message || 'SMS sent successfully',
          messageId: response.data.sid,
          channel: 'sms',
        };
      } else {
        throw new Error(`Channel '${request.channel}' is not available in DEMO mode. Only SMS is supported.`);
      }
    }
    
    // Live mode: Use production endpoints
    if (mode === 'live') {
      // Production endpoints based on channel
      let endpoint = '';
      let payload: any = {};
      
      switch (request.channel) {
        case 'whatsapp':
          endpoint = '/communication/whatsapp/send-text';
          payload = {
            to: request.to,
            text: request.message,
          };
          break;
        case 'email':
          endpoint = '/communication/email/send';
          payload = {
            to: request.to,
            subject: request.subject || 'Message from Bonyad',
            textContent: request.message,
          };
          break;
        case 'sms':
          endpoint = '/communication/sms/send';
          payload = {
            to: request.to,
            body: request.message,
          };
          break;
      }
      
      if (endpoint && request.to) {
        const response = await api.post(endpoint, payload);
        return {
          success: response.data.success !== false,
          message: response.data.message || 'Message sent successfully',
          messageId: response.data.messageId || response.data.sid,
          channel: request.channel,
        };
      } else {
        throw new Error(`Missing 'to' field for ${request.channel} channel`);
      }
    }
    
    // Fallback to admin communication endpoint (requires userId)
    if (!request.userId) {
      throw new Error("'userId' is required when using admin communication endpoint");
    }
    const response = await api.post('/admin/communication/send', request);
    return response.data;
  },

  // Get communication history for a user
  getHistory: async (userId: number, channel?: 'email' | 'whatsapp' | 'sms'): Promise<{
    success: boolean;
    data: CommunicationHistory[];
  }> => {
    const params: { userId: number; channel?: string } = { userId };
    if (channel) {
      params.channel = channel;
    }
    const response = await api.get('/admin/communication/history', { params });
    return response.data;
  },

  // Get available channels for a user
  getAvailableChannels: async (userId: number): Promise<{
    success: boolean;
    data: {
      email?: string;
      phone?: string;
      whatsapp?: string;
      channels: Array<'email' | 'whatsapp' | 'sms'>;
    };
  }> => {
    const response = await api.get(`/admin/communication/user/${userId}/channels`);
    return response.data;
  },

  // Get all services status (for live mode)
  getServicesStatus: async (): Promise<{
    success: boolean;
    data: {
      whatsapp?: { configured: boolean; testMode: boolean };
      facebook?: { configured: boolean; testMode: boolean };
      telegram?: { configured: boolean; testMode: boolean };
      email?: { configured: boolean; testMode: boolean };
      sms?: { configured: boolean; testMode: boolean };
    };
  }> => {
    try {
      const response = await api.get('/communication/status');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        data: {},
      };
    }
  },
};

